import { Form, Input } from "antd";
import React, { useState, useEffect } from "react";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { useSelector } from "react-redux";
import useMultiNotification from "../../hooks/useNotification";
import { updateProfileApi, getMyProfile } from "../../redux-toolkit/slices/authSlice";

export default function UpdateProfileForm() {
  const { contextHolder, openNotification } = useMultiNotification();
  const [phoneValue, setPhoneValue] = useState("");

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { userProfile, isSubmitting } = useSelector(
    (state: RootState) => state.auth
  ) as { userProfile: IUserProfile | null; isSubmitting: boolean };

  const onFinish = async (values: any) => {
    try {
      const {
        firstName,
        lastName,
        phoneNumber,
        street1,
        street2,
        city,
        state,
        zipCode,
      } = values;
      const data: IUpdateProfileApi = {
        firstName,
        lastName,
        phoneNumber,
        address: {
          state,
          city,
          zipCode,
          street1,
          street2: street2 ? street2 : null,
        },
      };
      const apiRes = await dispatch(updateProfileApi(data)).unwrap();
      // Refresh user profile data to update avatar initials
      await dispatch(getMyProfile());
      openNotification("success", "Success", apiRes.message);
    } catch (error) {
      if (typeof error === "string") {
        openNotification(`error`, `Update Failed`, error);
      } else {
        openNotification(`error`, `Update Failed`, `Server Error`);
      }
    }
  };
  const onFinishFailed = (data: any) => {};

  // Initialize phone value when userProfile loads
  useEffect(() => {
    if (userProfile?.phoneNumber) {
      setPhoneValue(userProfile.phoneNumber);
    }
  }, [userProfile]);

  if (!userProfile) {
    return <>Loading</>;
  }
  return (
    <>
      {contextHolder}
      <div className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden relative w-full" style={{ backgroundColor: "var(--neutral-100)" }}>
        <h3 className="h3 mb-3 w-full" style={{ textAlign: 'left' }}>
          Update Profile
        </h3>
        
        {/* Description Text */}
        <div style={{ textAlign: 'left' }}>
          <p className="body1 mb-6">
            Keep your personal information up to date for the best experience.
          </p>
        </div>

        {/* Wavy Line Separator */}
        <div className="flex justify-start mb-6">
          <svg viewBox="0 0 122 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[92px] md:w-[122px]">
            <path
              opacity="0.4"
              d="M1.46484 6.83613L4.45387 3.7103C7.74598 0.267505 13.38 0.760513 16.0241 4.72277L16.5428 5.50001C19.2423 9.54539 25.1877 9.54539 27.8873 5.5V5.5C30.5869 1.45461 36.5322 1.45461 39.2318 5.5V5.5C41.9314 9.54539 47.8768 9.54539 50.5764 5.5V5.5C53.2759 1.45461 59.2213 1.45461 61.9209 5.5V5.5C64.6205 9.54539 70.5658 9.54539 73.2654 5.5V5.5C75.965 1.45461 81.9104 1.45461 84.61 5.5V5.5C87.3096 9.54539 93.2549 9.54539 95.9545 5.5V5.5C98.6541 1.45461 104.599 1.45461 107.299 5.5V5.5C109.999 9.54539 115.944 9.54539 118.644 5.5L120.534 2.66667"
              stroke="var(--primary-500)"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <Form
          name="updateProfileForm"
          initialValues={{
            firstName: userProfile.firstName,
            lastName: userProfile.lastName,
            phoneNumber: userProfile.phoneNumber,
            street1: userProfile.address.street1,
            street2: userProfile.address.street2,
            city: userProfile.address.city,
            state: userProfile.address.state,
            zipCode: userProfile.address.zipCode,
          }}
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          requiredMark={false}
        >
          <div className="flex gap-4 w-full">
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: "What is your first name?" }]}
              className="flex-1 mb-0"
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="First Name" />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[{ required: true, message: "What is your last name?" }]}
              className="flex-1 mb-0"
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="Last Name" />
            </Form.Item>
          </div>

          <div className="flex gap-4 w-full">
            <Form.Item
              name="phoneNumber"
              rules={[
                { required: true, message: "What is your phone number?" },
                { 
                  pattern: /^\(\d{3}\) \d{3}-\d{4}$/, 
                  message: "Please enter a valid phone number" 
                }
              ]}
              className="flex-1 mb-0"
              style={{ marginBottom: 0 }}
            >
              <Input 
                placeholder="Phone Number" 
                value={phoneValue}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                  let formatted = value;
                  
                  if (value.length >= 6) {
                    formatted = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
                  } else if (value.length >= 3) {
                    formatted = `(${value.slice(0, 3)}) ${value.slice(3)}`;
                  } else if (value.length > 0) {
                    formatted = `(${value}`;
                  }
                  
                  setPhoneValue(formatted);
                  form.setFieldValue('phoneNumber', formatted);
                }}
                maxLength={14}
              />
            </Form.Item>
            <div className="flex-1"></div>
          </div>

          <Form.Item
            name="street1"
            rules={[{ required: true, message: "What is your Address1?" }]}
          >
            <Input placeholder="Street1/Address1" />
          </Form.Item>

          <Form.Item
            name="street2"
            // rules={[{ required: true, message: "What is phone Address1?" }]}
          >
            <Input placeholder="Apartment/Suite/Unit" />
          </Form.Item>

          <div className="flex gap-4 w-full">
            <Form.Item
              name="city"
              rules={[{ required: true, message: "What is your city?" }]}
              className="flex-1 mb-0"
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="City" />
            </Form.Item>
            <Form.Item
              name="state"
              rules={[{ required: true, message: "What is your state?" }]}
              className="mb-0"
              style={{ marginBottom: 0, width: '80px' }}
            >
              <Input placeholder="State" maxLength={2} />
            </Form.Item>
            <Form.Item
              name="zipCode"
              rules={[
                { required: true, message: "What is your zip code?" },
                { 
                  pattern: /^\d{5}(-\d{4})?$/, 
                  message: "Please enter a valid zip code (12345 or 12345-6789)" 
                }
              ]}
              className="mb-0"
              style={{ marginBottom: 0, width: '100px' }}
            >
              <Input placeholder="Zip Code" maxLength={10} />
            </Form.Item>
          </div>

          <Form.Item>
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary btn-lg"
              >
                {isSubmitting ? "Updating..." : "Update Profile"}
              </button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}
